import React, { useState, useEffect, useRef } from "react";
import Form from "./Form";
import aboutImg2 from "../images/live2.jpg";
import aboutImg3 from "../images/live3.jpg";
import aboutImg4 from "../images/live1.png";

const CARDS = [
  {
    index: "01",
    title: "Discovery Call (Complimentary)",
    duration: "15 Minutes",
    format: "Online / In-Person",
    tags: [
      "First-time clients",
      "Exploring coaching",
      "Gaining initial clarity",
    ],
    description:
      "A complimentary, no-obligation session to understand your current challenges, explore your goals, and determine if coaching is the right fit for you.",
    img: aboutImg2,
    btnLabel: "Schedule a Free Discovery Call",
    service_title: "Schedule a Free Discovery Call",
  },
  {
    index: "02",
    title: "1:1 Coaching Session",
    duration: "30 minutes",
    format: "Online / In-Person",
    tags: [
      "Gain clear direction",
      "Learn practical tools and strategies",
      "Build confidence to move forward",
    ],
    description:
      "A focused, personalized session designed to help you gain clarity, shift perspective, and take meaningful, actionable steps forward.",
    img: aboutImg3,
    btnLabel: "Book a Paid Session",
    service_title: "1:1 Coaching Session",
  },
  {
    index: "03",
    title: "Transformation Coaching Program (3 - 6 Months)",
    duration: "3-6 months",
    format: "Online",
    tags: [
      "Regular one-on-one coaching sessions",
      "Personalized action plans aligned to your goals",
      "Ongoing support and accountability",
    ],

    bestSuited: (
      <>
        <span style={{}}>Navigating life or career transitions</span> <br />
        <span style={{}}>Building confidence and clarity</span>
        <br />
        <span style={{}}>Long-term personal and professional growth</span>
      </>
    ),
    description:
      "A structured, long-term coaching engagement designed to support deep, sustainable transformation through consistent guidance and accountability.",
    img: aboutImg4,
    btnLabel: "Explore the Program",
    service_title: "Transformation Coaching Program",
  },
];

const TOTAL_CARDS = CARDS.length;
const DESKTOP_BREAKPOINT = 1200;

const Subscription = (onOpen) => {
  const [showForm, setShowForm] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const [isMobile, setIsMobile] = useState(false);

  const outerRef = useRef(null);
  const leftInfoRefs = useRef([]);
  const rightImgRefs = useRef([]);
  const currentCard = useRef(0);
  const gsapRef = useRef(null);
  const scrollBound = useRef(null);

  // ── Detect mobile on mount + resize ────────────────────────────────────────
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < DESKTOP_BREAKPOINT);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // ── Desktop: load GSAP + init card states ──────────────────────────────────
  useEffect(() => {
    if (isMobile) return; // skip on mobile

    const load = () => {
      if (window.gsap) {
        gsapRef.current = window.gsap;
        initStates();
        return;
      }
      const g = document.createElement("script");
      g.src = "https://cdnjs.cloudflare.com/ajax/libs/gsap/3.12.5/gsap.min.js";
      g.async = true;
      document.head.appendChild(g);
      g.onload = () => {
        gsapRef.current = window.gsap;
        initStates();
      };
    };

    const initStates = () => {
      const gsap = gsapRef.current;
      if (!gsap) return;
      leftInfoRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.set(el, { opacity: i === 0 ? 1 : 0, y: i === 0 ? 0 : 24 });
      });
      rightImgRefs.current.forEach((el, i) => {
        if (!el) return;
        gsap.set(el, {
          clipPath: i === 0 ? "inset(0% 0% 0% 0%)" : "inset(100% 0% 0% 0%)",
        });
      });
    };

    load();
  }, [isMobile]);

  // ── Desktop: scroll listener ───────────────────────────────────────────────
  useEffect(() => {
    if (isMobile) {
      // Clean up any previous scroll listener when switching to mobile
      if (scrollBound.current) {
        window.removeEventListener("scroll", scrollBound.current);
        scrollBound.current = null;
      }
      // Reset card states to visible for mobile
      const gsap = gsapRef.current;
      if (gsap) {
        leftInfoRefs.current.forEach(
          (el) => el && gsap.set(el, { opacity: 1, y: 0 }),
        );
        rightImgRefs.current.forEach(
          (el) => el && gsap.set(el, { clipPath: "inset(0% 0% 0% 0%)" }),
        );
      }
      currentCard.current = 0;
      setActiveIdx(0);
      return;
    }

    const onScroll = () => {
      const outer = outerRef.current;
      if (!outer || !gsapRef.current) return;

      const rect = outer.getBoundingClientRect();
      const totalH = outer.offsetHeight - window.innerHeight;
      const scrolled = -rect.top;
      const progress = Math.max(0, Math.min(1, scrolled / totalH));

      const segment = 1 / TOTAL_CARDS;
      const cardIndex = Math.min(
        Math.floor(progress / segment),
        TOTAL_CARDS - 1,
      );

      if (cardIndex !== currentCard.current) {
        const from = currentCard.current;
        currentCard.current = cardIndex;
        setActiveIdx(cardIndex);
        transition(from, cardIndex);
      }
    };

    scrollBound.current = onScroll;
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isMobile]);

  // ── Transition (desktop only) ─────────────────────────────────────────────
  const transition = (from, to) => {
    const gsap = gsapRef.current;
    if (!gsap) return;

    leftInfoRefs.current.forEach((el) => el && gsap.killTweensOf(el));
    rightImgRefs.current.forEach((el) => el && gsap.killTweensOf(el));

    leftInfoRefs.current.forEach((el, i) => {
      if (!el || i === to) return;
      gsap.set(el, { opacity: 0, y: 0 });
    });

    const toLeft = leftInfoRefs.current[to];
    if (toLeft)
      gsap.fromTo(
        toLeft,
        { opacity: 0, y: to > from ? 24 : -24 },
        { opacity: 1, y: 0, duration: 0.4, ease: "power2.out" },
      );

    rightImgRefs.current.forEach((el, i) => {
      if (!el || i === to) return;
      gsap.set(el, { clipPath: "inset(100% 0% 0% 0%)" });
    });

    const toImg = rightImgRefs.current[to];
    const forward = to > from;
    if (toImg)
      gsap.fromTo(
        toImg,
        { clipPath: forward ? "inset(100% 0% 0% 0%)" : "inset(0% 0% 100% 0%)" },
        { clipPath: "inset(0% 0% 0% 0%)", duration: 0.5, ease: "power2.out" },
      );
  };

  // ── MOBILE RENDER — plain stacked cards, no animation ─────────────────────
  if (isMobile) {
    return (
      <>
        <section id="services" className="animate services mxd-pinned-projects">
          <div className="container px-4">
            <p
              style={{
                fontSize: "16px",
                opacity: 0.45,
                letterSpacing: "0.12em",
                marginBottom: "32px",
              }}
            >
              Coaching Programs
            </p>

            {CARDS.map((card, i) => (
              <div key={i} className="ap-card" style={{ marginBottom: "32px" }}>
                <img
                  src={card.img}
                  alt={card.title}
                  style={{
                    width: "100%",
                    objectFit: "cover",
                    borderRadius: "12px",
                    marginBottom: "20px",
                    color: "black",
                  }}
                />
                <span
                  style={{
                    fontSize: "16px",
                    opacity: 0.35,
                    letterSpacing: "0.1em",
                    display: "block",
                    marginBottom: "10px",
                    color: "black",
                  }}
                >
                  {card.index} / 0{TOTAL_CARDS}
                </span>
                <h3
                  style={{
                    fontSize: "1.4rem",
                    marginBottom: "16px",
                    lineHeight: 1.2,
                    color: "black",
                  }}
                >
                  {card.title}
                </h3>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                    marginBottom: "16px",
                    color: "black",
                  }}
                >
                  {card.tags.map((tag) => (
                    <span
                      key={tag}
                      style={{
                        padding: "4px 12px",
                        borderRadius: "999px",
                        border: "1px solid currentColor",
                        fontSize: "16px",
                        opacity: 0.6,
                        color: "black",
                      }}
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <p
                  style={{
                    marginBottom: "4px",
                    fontSize: "16px",
                    color: "black",
                  }}
                >
                  <strong>Duration:</strong> {card.duration}
                </p>
                <p
                  style={{
                    marginBottom: "16px",
                    fontSize: "16px",
                    color: "black",
                  }}
                >
                  <strong>Session Type:</strong> {card.format}
                </p>
                <p
                  style={{
                    marginBottom: "20px",
                    fontSize: "16px",
                    color: "black",
                  }}
                >
                  <strong>Best suited for:</strong> <br />
                  {card.bestSuited}
                </p>
                <p
                  style={{
                    opacity: 0.7,
                    lineHeight: 1.7,
                    marginBottom: "24px",
                    color: "black",
                  }}
                >
                  {card.description}
                </p>
                <button
                  type="button"
                  className="btn-new"
                  onClick={() =>
                    setShowForm({
                      service_title: card.service_title,
                      duration: card.duration,
                      format: card.format,
                    })
                  }
                  style={{ border: "1px solid #fff", cursor: "pointer" }}
                >
                  {card.btnLabel}
                </button>
              </div>
            ))}
          </div>
        </section>

        <Form
          isOpen={!!showForm}
          onClose={() => setShowForm(false)}
          serviceDetails={showForm}
        />
      </>
    );
  }

  // ── DESKTOP RENDER — sticky scroll animation ───────────────────────────────
  return (
    <>
      <div
        ref={outerRef}
        style={{ height: `${TOTAL_CARDS * 100}vh`, position: "relative" }}
      >
        <div
          id="services"
          className="animate services mxd-pinned-projects"
          style={{
            position: "sticky",
            top: 0,
            height: "100vh",
            overflow: "hidden",
          }}
        >
          <div className="container px-4 px-lg-5" style={{ height: "100%" }}>
            <div
              className="row g-0 align-items-stretch"
              style={{ height: "100%" }}
            >
              {/* LEFT col */}
              <div
                className="col-12 col-xl-8"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  // paddingTop: "80px",
                  // paddingBottom: "80px",
                  paddingRight: "48px",
                  position: "relative",
                }}
              >
                <p
                  style={{
                    fontSize: "16px",
                    opacity: 0.45,
                    letterSpacing: "0.12em",
                    marginBottom: "32px",
                  }}
                >
                  Coaching Programs
                </p>
                <div style={{ position: "relative" }}>
                  {CARDS.map((card, i) => (
                    <div
                      key={i}
                      className="mxd-pinned-projects__info"
                      ref={(el) => (leftInfoRefs.current[i] = el)}
                      style={{
                        position: i === 0 ? "relative" : "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        pointerEvents: activeIdx === i ? "auto" : "none",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "16px",
                          opacity: 0.35,
                          letterSpacing: "0.1em",
                          display: "block",
                          marginBottom: "16px",
                        }}
                      >
                        {card.index} / 0{TOTAL_CARDS}
                      </span>
                      <h3
                        style={{
                          fontSize: "clamp(1.4rem, 2.5vw, 2rem)",
                          marginBottom: "20px",
                          lineHeight: 1.2,
                        }}
                      >
                        {card.title}
                      </h3>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "8px",
                          marginBottom: "24px",
                        }}
                      >
                        {card.tags.map((tag) => (
                          <span
                            key={tag}
                            style={{
                              padding: "5px 16px",
                              borderRadius: "999px",
                              border: "1px solid currentColor",
                              fontSize: "16px",
                              opacity: 0.6,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                      <p style={{ marginBottom: "4px", fontSize: "16px" }}>
                        <strong>Duration:</strong> {card.duration}
                      </p>
                      <p style={{ marginBottom: "20px", fontSize: "16px" }}>
                        <strong>Session Type:</strong> {card.format}
                      </p>
                      <p style={{ marginBottom: "20px", fontSize: "16px" }}>
                        {card.bestSuited}
                      </p>
                      <p
                        style={{
                          opacity: 0.7,
                          lineHeight: 1.7,
                          marginBottom: "32px",
                        }}
                      >
                        {card.description}
                      </p>
                      <button
                        type="button"
                        className="btn-new"
                        onClick={() =>
                          setShowForm({
                            service_title: card.service_title,
                            duration: card.duration,
                            format: card.format,
                          })
                        }
                        style={{ border: "1px solid #fff", cursor: "pointer" }}
                      >
                        {card.btnLabel}
                      </button>
                    </div>
                  ))}
                </div>

                {/* Progress dots */}
                <div
                  className="sub-progress-dots"
                  style={{
                    display: "flex",
                    gap: "8px",
                    position: "absolute",
                    bottom: "40px",
                    left: 0,
                  }}
                >
                  {CARDS.map((_, i) => (
                    <span
                      key={i}
                      style={{
                        width: activeIdx === i ? "28px" : "8px",
                        height: "8px",
                        borderRadius: "999px",
                        background: "currentColor",
                        opacity: activeIdx === i ? 1 : 0.25,
                        transition: "all 0.4s ease",
                        display: "inline-block",
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* RIGHT col */}
              <div
                className="col-12 col-xl-4 mxd-pinned-projects__scroll"
                style={{
                  position: "relative",
                  height: "100%",
                  overflow: "hidden",
                }}
              >
                {CARDS.map((card, i) => (
                  <div
                    key={i}
                    className="mxd-pinned-projects__item"
                    style={{
                      position: "absolute",
                      inset: 0,
                      overflow: "hidden",
                    }}
                  >
                    <div
                      ref={(el) => (rightImgRefs.current[i] = el)}
                      className="subsriptionImg"
                      style={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={card.img}
                        alt={card.title}
                        style={{
                          width: "100%",
                          objectFit: "cover",
                          display: "block",
                        }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <Form
        isOpen={!!showForm}
        onClose={() => setShowForm(false)}
        serviceDetails={showForm}
      />
    </>
  );
};

export default Subscription;
